import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App';
import { ThemeProvider } from '@/components/ThemeProvider';
import { queryClient } from '@/lib/query-client';
import { migrateThemeToLightOnce } from '@/lib/theme-migration';
// Self-hosted Nunito (400/600/700/800) — production must not depend on
// Google Fonts at runtime; @fontsource bundles the woff2 files locally.
import '@fontsource/nunito/400.css';
import '@fontsource/nunito/600.css';
import '@fontsource/nunito/700.css';
import '@fontsource/nunito/800.css';
import './index.css';

// iCGChat console entrypoint. Composes the global providers in the order
// the rest of the app expects:
//   ThemeProvider   — dark/light/system theme (next-themes; outermost so the
//                     `.dark` class lands on <html> before any child renders)
//   QueryClient     — server-state cache (React Query)
//   BrowserRouter   — client-side routing (react-router-dom v6)
//   ReactQueryDevtools — opt-in panel in dev mode (`initialIsOpen=false`)
const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}

// Neumorphic-reskin rollout: force next-themes to "light" once per browser
// BEFORE ThemeProvider's first render reads localStorage — see
// `lib/theme-migration.ts` for the full rationale. Must run here, ahead of
// `createRoot(...).render(...)`, so there's no flash of the old theme.
migrateThemeToLightOnce();

createRoot(rootEl).render(
  <StrictMode>
    {/* defaultTheme="light": the neumorphic soft-UI design IS the light
        theme and is now the console default. Users can still pick Dark or
        System from the ThemeToggle (persisted by next-themes as before). */}
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
